import React, { useState } from 'react';
import './fournisseurs.css';
import HeaderButton from 'utils/headerButton';
import CustomBootstrapTable from 'utils/reactTable';
import HeaderFirst from 'utils/HeaderFirst';
import { FormProvider, useForm } from 'react-hook-form';
import ModalFournisseur from './Modal';
import arrow from '../../image/arrow-left.png';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import useFournisseurData from './useFournisseurData';
import { toast } from 'react-toastify';
import TablePaginators from 'components/Pagination/TablePaginator';
import ModalExcel from './ModalExcel/ModalExcel';
import { HiCloudUpload } from 'react-icons/hi';
import DownloadExcel from 'pagesSAGECOMPTA/FileExcel/FileExcel';
import ButtonWhite from 'components/buttons/ButtonWhite';
import TableLoader from 'components/Loader/TableLoader';
import { useDossierRole } from 'contexts/DossierRoleContext';
//import { HiCloudUpload } from "react-icons/hi";


function Fournisseurs() {
  const { clientID: dossier_id } = useParams();
  const { canEdit } = useDossierRole();
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(10);
  const [paginationIndex, setPaginationIndex] = useState(0);
  const [selectedRows, setSelectedRows] = useState([]);
  const { updatedFournisseurs, handleUpdateFournisseur, handleDeleteFournisseur, refetch, isLoading } =
    useFournisseurData(dossier_id);
  const [viewArchived, setViewArchived] = useState(false);
  const navigate = useNavigate();
  const data_from_ecriture_comptable = useLocation().state?.comeFrom || false

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalFileOpen, setIsModalFileOpen] = useState(false)

  // Fonction pour formater la date au format "Année-Jour-Mois"
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString; // Retourne la date originale si invalide
    
    const year = date.getFullYear();
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    
    return `${year}-${day}-${month}`;
  };

  const filteredData = updatedFournisseurs
    .filter((fournisseur) =>
      viewArchived ? fournisseur.archived : !fournisseur.archived
    )
    .filter((fournisseur) =>
      fournisseur.nom.toLowerCase().includes(searchTerm.toLowerCase())
    );

  const currentPageData = filteredData.slice(
    paginationIndex * pageSize,
    (paginationIndex + 1) * pageSize
  );

  const handleActionSelected = (action) => {
    if (action === 'archive') {
      setViewArchived(!viewArchived);
    } else {
      console.log(`Action selected: ${action}`);
      // Handle other actions as necessary
    }
  };

  const handlePageChange = (newIndex) => {
    setPaginationIndex(newIndex);
  };

  const handleSearchChange = (newSearchTerm) => {
    setSearchTerm(newSearchTerm);
  };

  const handleRowSelect = (row, isSelect) => {
    if (isSelect) {
      setSelectedRows((prev) => [...prev, row.id]);
    } else {
      setSelectedRows((prev) => prev.filter((id) => id !== row.id));
    }
  };

  const onClickDetails = async () => {
    if (selectedRows.length === 1) {
      const selectedRowId = selectedRows[0];
      const detailFournisseurURL = `details/${selectedRowId}/`;
      navigate(detailFournisseurURL);
    }
  };

  const onClickArchive = async () => {
    if (selectedRows.length === 0) {
      toast.warning('Veuillez sélectionner au moins un fournisseur à archiver.');
      return;
    }

    try {
      // Déterminer le statut basé sur le premier fournisseur sélectionné
      const firstFournisseur = updatedFournisseurs.find((f) => f.id === selectedRows[0]);
      if (!firstFournisseur) {
        toast.error('Fournisseur non trouvé.');
        return;
      }

      // Si le fournisseur est actuellement archivé, on désarchive (active: true, archived: false)
      // Sinon on archive (active: false, archived: true)
      const shouldArchive = !firstFournisseur.archived;
      
      // Archiver/Désarchiver tous les fournisseurs sélectionnés
      const updatePromises = selectedRows.map((id) => {
        const fournisseur = updatedFournisseurs.find((f) => f.id === id);
        if (fournisseur) {
          return handleUpdateFournisseur(id, {
            active: !shouldArchive,    // false si on archive, true si on désarchive
            archived: shouldArchive     // true si on archive, false si on désarchive
          }).catch(error => {
            console.error(`Erreur mise à jour fournisseur ${id}:`, error);
            return { error, id };
          });
        }
        return Promise.resolve();
      });

      const results = await Promise.all(updatePromises);
      
      // Vérifier s'il y a eu des erreurs
      const errors = results.filter(r => r && r.error);
      const successes = selectedRows.length - errors.length;
      
      if (successes > 0) {
        toast.success(
          `${successes} fournisseur(s) ${shouldArchive ? 'archivé(s)' : 'désarchivé(s)'} avec succès.`
        );
      }
      
      if (errors.length > 0) {
        toast.error(`${errors.length} fournisseur(s) n'ont pas pu être mis à jour.`);
      }
      
      setSelectedRows([]); // Réinitialiser la sélection
    } catch (error) {
      toast.error(
        'Une erreur est survenue lors de la mise à jour des fournisseurs.'
      );
      console.error('Erreur lors de la mise à jour des fournisseurs :', error);
    }
  };

  const onClickDelete = async () => {
    if (selectedRows.length === 1) {
      const selectedRowId = selectedRows[0];
      const fournisseur = updatedFournisseurs.find(
        (f) => f.id === selectedRowId
      );
      if (!fournisseur) {
        toast.error('Fournisseur non trouvé.');
        return;
      }

      try {
        await handleDeleteFournisseur(selectedRowId);
        setSelectedRows([]);
      } catch (error) {
        toast.error(
          'Une erreur est survenue lors de la suppression du fournisseur.'
        );
      }
    } else {
      toast.warning(
        'Veuillez sélectionner exactement un fournisseur à supprimer.'
      );
    }
  };

  const columns = [
    {
      dataField: 'compte_comptable',
      text: 'N°Compte',
      formatter: (cell) => <div>{cell}</div>,
    },
    {
      dataField: 'nom',
      text: 'Nom',
      formatter: (cell) => <div>{cell}</div>,
    },
    {
      dataField: 'created_at',
      text: "Date d'ajout",
      formatter: (cell) => <div>{formatDate(cell)}</div>,
    },
    {
      dataField: 'telephone',
      text: 'Numéro de téléphone',
      formatter: (cell) => <div>{cell}</div>,
    },
    {
      dataField: 'email',
      text: 'E-mail',
      formatter: (cell) => <div>{cell}</div>,
    },
    {
      dataField: 'active',
      text: 'Statut',
      formatter: (cell) => (cell ? 'Actif' : 'Inactif'),
    },
  ];

  const handleDossierCreated = async () => {
    // Rafraîchir les données
    await refetch();
    setIsModalOpen(false);
  };

  const nom = (
    <div style={{ display: 'flex' }}>
      <h4 className="titre-dossier">{viewArchived ? 'Fournisseurs archivés' : 'Fournisseurs'}</h4>
      <span className="nbrDossier">{filteredData?.length}</span>
    </div>
  );

  const description = <div>Voici tous vos fournisseurs {viewArchived && 'archivés'}</div>;


  const closeModal = () => {
    setIsModalOpen(false);
    setIsModalFileOpen(false)

  };

  const openModal = () => {
    setIsModalOpen(true);
    setIsModalFileOpen(false);
  };

  const methods = useForm();

  return (
    <div
      className="global"
      style={{
        backgroundColor: 'white',
        minHeight: '500px',
        borderRadius: '20px',
        padding: '15px',
      }}
    >
      <div
        className="header mb-3"
        style={{
          backgroundColor: 'white',
          borderRadius: '10px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          flexWrap: 'wrap',
        }}
      >
        <HeaderFirst nom={nom} description={description} />
        {canEdit && (
          <>
            <div>
              <ButtonWhite
                text={
                  <span style={ajoutFou}><HiCloudUpload className='fs-6' /> Importer des fournisseurs</span>
                }
                onClick={() => { setIsModalFileOpen(true); setIsModalOpen(false) }} />
            </div>
            <div>
              <ButtonWhite
                text="+ Ajouter un fournisseur"
                onClick={openModal} />
            </div>
          </>
        )}
        <DownloadExcel />
        {isModalFileOpen && (
          <FormProvider {...methods}>
            <ModalExcel
              isOpen={isModalFileOpen}
              onClose={closeModal}
              refetch={refetch}
              retour={data_from_ecriture_comptable}
            >
              <h2
                style={{ color: ' #009688', fontSize: '20px', fontWeight: 700 }}
              >
                <span>
                  {' '}
                  <img src={arrow} />
                </span>{' '}
                &nbsp;IMPORTER VOS FICHIERS CLIENTS
              </h2>
            </ModalExcel>
          </FormProvider>

        )}

        {isModalOpen && (
          <FormProvider {...methods}>
            <ModalFournisseur
              isOpen={isModalOpen}
              onClose={closeModal}
              onDossierCreated={handleDossierCreated}
              //fournisseurs={updatedFournisseurs}
              retour={data_from_ecriture_comptable}
              refetch={refetch}
            >
              <h2 style={{ color: '#009688', fontSize: '20px', fontWeight: 700 }}>
                <img src={arrow} alt="Arrow" />
                &nbsp;CREER UN NOUVEAU DOSSIER FOURNISSEUR
              </h2>
            </ModalFournisseur>
          </FormProvider>
        )}
      </div>
      <HeaderButton
        placeholder={'Rechercher un fournisseur'}
        onSearchChange={handleSearchChange}
        isButtonsEnabled={selectedRows?.length > 0}
        onClickArchive={onClickArchive}
        onEyesClick={onClickDetails}
        onClickDelete={onClickDelete}
      />
      <div>
        {isLoading && (!updatedFournisseurs || updatedFournisseurs.length === 0) ? (
          <div style={{ background: '#ffffff', borderRadius: '12px', padding: '30px', border: '1px solid #e2eced' }}>
            <TableLoader
              message="Chargement des fournisseurs..."
              subMessage="Récupération du répertoire fournisseurs et des comptes rattachés"
              minHeight="280px"
            />
          </div>
        ) : updatedFournisseurs ? (
          <CustomBootstrapTable
            keyField="id"
            data={currentPageData}
            columns={columns}
            selectedRows={selectedRows}
            onSelectRow={handleRowSelect}
            wrapperClasses="table-wrapper"
          />
        ) : null}
      </div>
      {!isLoading && (!currentPageData || currentPageData.length === 0) && (
        <div className="d-flex justify-content-center fs-5 mt-4 text-muted">
          <p>Aucun fournisseur à afficher.</p>
        </div>
      )}
      {updatedFournisseurs && updatedFournisseurs.length > 0 && (
        <TablePaginators
          paginationIndex={paginationIndex}
          setPaginationIndex={handlePageChange}
          dataLength={filteredData.length}
          pageSize={pageSize}
          setPageSize={setPageSize}
          onActionSelected={handleActionSelected}
          isViewingArchived={viewArchived}
        />
      )}
    </div>
  );
}

const ajoutFou = {
  fontFamily: 'Montserrat',
  fontWeight: '600',
  fontSize: '13px',
};

export default Fournisseurs;
